// Board TILE / GRID construction + layout — geometry, grid build, idle board life, gel material,
// label text-fit. Sub-split from boardView.ts (FACE&FURY S6, behavior-frozen): bodies byte-identical
// to the monolith; only the containing file changed. Bound onto GameScene via the boardView.ts barrel.
import Phaser from 'phaser';
import type { Snapshot } from '../../core';
import { PALETTE, hex, FONT, tex, hasTex } from '../keys';
import { motionAllowed, scaledFont } from '../settings';
import { isLandscape, LANDSCAPE_GUTTER_W } from '../stage';
import { WORD_BREAK_MAX_PX, mixColor, type CellView } from './types';
import type { GameScene } from '../scenes/GameScene';


function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}


function paintBlockedSocketMark(g: Phaser.GameObjects.Graphics, cx: number, cy: number, w: number, h: number, accent: number): void {
  const r = Math.min(w, h) * 0.19;
  const lineW = clamp(Math.floor(w * 0.035), 2, 4);
  g.fillStyle(PALETTE.wall, 0.92);
  g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 8);
  g.lineStyle(1.5, 0x000000, 0.42);
  g.strokeRoundedRect(cx - w / 2 + 1.5, cy - h / 2 + 1.5, w - 3, h - 3, 7);
  g.lineStyle(1, accent, 0.5);
  g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 8);

  const iconY = cy - h * 0.08;
  g.lineStyle(lineW, PALETTE.lockGlyph, 0.82);
  g.strokeCircle(cx, iconY, r);
  g.lineStyle(lineW + 1, PALETTE.wall, 1);
  g.lineBetween(cx - r * 0.7, iconY + r * 0.7, cx + r * 0.7, iconY - r * 0.7);
  g.lineStyle(lineW, PALETTE.lockGlyph, 0.9);
  g.lineBetween(cx - r * 0.62, iconY + r * 0.62, cx + r * 0.62, iconY - r * 0.62);

  const hatchA = Math.min(w, h) * 0.28;
  const hatchB = Math.min(w, h) * 0.17;
  g.lineStyle(1, accent, 0.32);
  g.lineBetween(cx - hatchA, cy - hatchA, cx - hatchB, cy - hatchA);
  g.lineBetween(cx - hatchA, cy - hatchA, cx - hatchA, cy - hatchB);
  g.lineBetween(cx + hatchA, cy + hatchA, cx + hatchB, cy + hatchA);
  g.lineBetween(cx + hatchA, cy + hatchA, cx + hatchA, cy + hatchB);
}


function makeBlockedSocketText(scene: GameScene, x: number, y: number, w: number): Phaser.GameObjects.Text {
  const baseSize = clamp(Math.floor(w * 0.13), 9, 13);
  const maxSize = clamp(Math.floor(w * 0.18), 12, 18);
  return scene.add
    .text(x, y, 'BLOCKED', {
      fontFamily: FONT.family,
      fontSize: `${Math.min(maxSize, scaledFont(baseSize, 9))}px`,
      color: hex(PALETTE.lockGlyph),
      fontStyle: 'bold',
      align: 'center',
    })
    .setOrigin(0.5)
    .setStroke('#020617', 3)
    .setShadow(0, 1, '#020617', 2, false, true);
}



  // --- layout --------------------------------------------------------------
export function computeLayout(this: GameScene): void {
    const W = this.scale.width;
    const H = this.scale.height;
    if (isLandscape()) {
      // LANDSCAPE STAGE (S4): the board is the hero — centred in the middle column between two gutter
      // rails (left = telemetry, right = command dock, both built by buildHud). Sized to the FULL height
      // budget so a 6×6 board reads large, then centred inside the centre column. GUTTER_W here is the
      // single source of truth the HUD rails also key off (kept in sync with hudController).
      const gutterW = LANDSCAPE_GUTTER_W;
      const bannerH = Math.min(72, H * 0.1);
      const topPad = bannerH + 16;
      const botPad = 26;
      const centerLeft = gutterW;
      const centerRight = W - gutterW;
      const centerW = centerRight - centerLeft;
      const padX = 20;
      const availW = centerW - padX * 2;
      const availH = H - topPad - botPad;
      const cell = Math.floor(Math.min(availW / this.cols, availH / this.rows));
      this.cellW = cell;
      this.cellH = cell;
      this.boardW = cell * this.cols;
      const boardH = cell * this.rows;
      this.gridX = centerLeft + (centerW - this.boardW) / 2;
      this.gridY = topPad + (availH - boardH) / 2;
      return;
    }
    const bannerH = Math.min(72, H * 0.1);
    const hudTopH = Math.min(64, H * 0.09);
    const hudBottomH = Math.max(110, Math.min(136, H * 0.2));
    const padX = Math.max(8, W * 0.03);

    const availW = W - padX * 2;
    const availH = H - bannerH - hudTopH - hudBottomH - 24;
    // Fit cols x rows keeping square-ish tiles.
    const cellByW = availW / this.cols;
    const cellByH = availH / this.rows;
    const cell = Math.floor(Math.min(cellByW, cellByH));
    this.cellW = cell;
    this.cellH = cell;
    this.boardW = cell * this.cols;
    const boardH = cell * this.rows;
    this.gridX = (W - this.boardW) / 2;
    // Tall portrait has more vertical slack than the board needs. Bias the board upward so the HUD
    // state band reads attached to the grid instead of separated by a contentless well.
    this.gridY = bannerH + hudTopH + (availH - boardH) * 0.44 + 4;
  }



  // --- grid ----------------------------------------------------------------
export function buildGrid(this: GameScene, snap: Snapshot): void {
    const boardH = this.cellH * this.rows;
    const pad = Math.max(8, Math.min(14, this.cellW * 0.12));
    const boardCx = this.scale.width / 2;
    const boardCy = this.gridY + boardH / 2;
    const panelW = this.boardW + pad * 2;
    const panelH = boardH + pad * 2;
    this.add
      .rectangle(boardCx, boardCy, panelW, panelH, PALETTE.surface, 0.72)
      .setStrokeStyle(2, PALETTE.surfaceBorder, 0.8)
      .setDepth(2);
    // PER-BOARD ATMOSPHERE: a thin decorative accent hairline around the board frame + a blended
    // NEUTRAL tile-border tone, keyed to the rung theme (deep blue -> indigo -> violet -> teal ->
    // ember). Decoration only: the gameplay state borders (correct/hazard/armed/scan) never change.
    const theme = this.boardTheme();
    this.themeCellStroke = mixColor(PALETTE.lineStrong, theme.accent, 0.3);
    // S6: the outer board FRAME tint derives from the topic-world bucket (blended with the rung accent
    // so both identities read) — the frame/corner ornament agree with the world behind the grid. The
    // NEUTRAL tile borders stay per-rung (themeCellStroke) so gameplay reads are unchanged.
    const frameAccent = mixColor(theme.accent, this.boardWorldAccent(), 0.5);
    this.add
      .rectangle(boardCx, boardCy, panelW + 8, panelH + 8, 0x000000, 0)
      .setStrokeStyle(1.5, frameAccent, 0.4)
      .setDepth(1.9);
    // PER-RUNG MOTIF, frame echo: a tiny corner ornament on the hairline (circuit node / diamond /
    // stream chamfer / hex cell / ember trail per rung) — see paintFrameCornerMotif's contract.
    this.paintFrameCornerMotif(boardCx, boardCy, panelW + 8, panelH + 8, frameAccent);

    // BOARD DEPTH / "lit cabinet" layer (renderer-only). ONE faint depth layer on the STAGE (not the
    // cells): a low-alpha radial glow pooled in the board centre + a soft inner-edge vignette, painted
    // BELOW the panel fill / cells (depth 1.5) so the grid reads as sitting inside a lit recess rather
    // than on flat navy. Built once (static graphics); under motion it drifts ever so slightly via the
    // scene UPDATE hook (no per-frame allocation — just a position nudge). Kept well under the cell text
    // band in both z-order AND alpha so it can never compete with the answer labels.
    {
      const depth = this.add.graphics().setDepth(1.5);
      const gx = boardCx;
      const gy = boardCy;
      // concentric centre glow — a few stacked low-alpha discs (additive) pooling light at the middle.
      depth.setBlendMode(Phaser.BlendModes.ADD);
      const glowR = Math.max(panelW, panelH) * 0.46;
      for (let i = 3; i >= 1; i--) {
        depth.fillStyle(PALETTE.player, 0.012 * i);
        depth.fillCircle(gx - boardCx, gy - boardCy, (glowR * i) / 3);
      }
      depth.setPosition(gx, gy);
      // inner-edge vignette — four faint dark bands hugging the panel rim so the recess reads as lit.
      const vg = this.add.graphics().setDepth(1.5);
      const vt = Math.max(10, Math.min(panelW, panelH) * 0.12);
      const left = boardCx - panelW / 2;
      const top = boardCy - panelH / 2;
      vg.fillStyle(0x000000, 0.14);
      vg.fillRect(left, top, panelW, vt); // top
      vg.fillRect(left, top + panelH - vt, panelW, vt); // bottom
      vg.fillRect(left, top, vt, panelH); // left
      vg.fillRect(left + panelW - vt, top, vt, panelH); // right
      this.boardDepth = depth;
      this.boardDepthBaseX = gx;
      this.boardDepthBaseY = gy;
      if (motionAllowed()) {
        // very subtle parallax: the centre glow drifts a couple of px so the cabinet light feels alive.
        const driftX = Math.max(3, panelW * 0.018);
        const driftY = Math.max(3, panelH * 0.014);
        const onUpdate = (_t: number, dt: number) => {
          if (!this.boardDepth || !this.boardDepth.active) return;
          this.boardDepthElapsed += dt;
          const s = this.boardDepthElapsed / 1000;
          this.boardDepth.x = this.boardDepthBaseX + Math.sin(s * 0.22) * driftX;
          this.boardDepth.y = this.boardDepthBaseY + Math.cos(s * 0.17) * driftY;
        };
        this.events.on(Phaser.Scenes.Events.UPDATE, onUpdate, this);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
          this.events.off(Phaser.Scenes.Events.UPDATE, onUpdate, this),
        );
      }
    }

    // Board ambient life: a soft top-down sheen + a faint inner-edge vignette painted ON the board
    // surface (depth 2.5 -> above the panel fill, below the cells at depth 3) so the playfield reads
    // as a lit, premium slab the tiles sit in — never competing with the cells themselves. Minimal,
    // low-alpha, edge-/top-weighted. Under motion it "breathes" via a very gentle alpha yoyo; under
    // reduced-motion it holds static (the static sheen alone carries the depth read).
    const ambient = this.add.graphics().setDepth(2.5);
    const ax = boardCx - panelW / 2;
    const ay = boardCy - panelH / 2;
    // top sheen — a couple of fading bands across the upper board edge.
    ambient.fillStyle(0xffffff, 0.03);
    ambient.fillRect(ax + 2, ay + 2, panelW - 4, panelH * 0.16);
    ambient.fillStyle(0xffffff, 0.018);
    ambient.fillRect(ax + 2, ay + 2 + panelH * 0.16, panelW - 4, panelH * 0.1);
    // bottom grounding shade — a thin dark band along the lower board edge.
    ambient.fillStyle(0x000000, 0.12);
    ambient.fillRect(ax + 2, ay + panelH - panelH * 0.1, panelW - 4, panelH * 0.1 - 2);
    if (motionAllowed()) {
      this.tweens.add({
        targets: ambient,
        alpha: { from: 0.78, to: 1 },
        duration: 3200,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }

    this.selectionRing = this.add.graphics().setDepth(8);

    for (const cell of snap.cells) {
      const { x, y } = this.cellCenter(cell.id);
      const container = this.add.container(x, y).setDepth(3);
      const w = this.cellW - 4;
      const h = this.cellH - 4;
      // Tile ART underlay (drawn first => behind everything). When wired, the bg rect above becomes a
      // translucent TINT layer (state colors at low alpha) so the art reads while the colored
      // eaten/armed/scanned signals AND the centered label still sit clearly on top.
      const useTileArt = cell.kind !== 'wall' && hasTex('cellNeutral');
      let tileImg: Phaser.GameObjects.Image | undefined;
      if (useTileArt) {
        tileImg = this.add.image(0, 0, tex('cellNeutral'));
        tileImg.setDisplaySize(w, h);
      }
      const bg = this.add
        .rectangle(0, 0, w, h, PALETTE.tile, useTileArt ? 0 : 1)
        .setStrokeStyle(1, this.themeCellStroke);
      const label = this.add
        // TWO-FACE SYSTEM (#28): answer tiles are the all-game READING surface, so they route through
        // FONT.family = the brand CONTENT face (Fredoka, warm rounded) — no longer the accidental OS
        // system-ui. setCellLabel's shrink-to-fit + WORD_BREAK stay untouched: the fitter MEASURES the
        // actually-rendered text (GetTextSize), so it self-adapts to Fredoka's slightly wider metrics
        // and the longest post-content-swap labels still fit whole with no truncation.
        .text(0, 0, '', {
          fontFamily: FONT.family,
          fontSize: '16px', // real size set by setCellLabel's shrink-to-fit below
          color: hex(PALETTE.white),
          align: 'center',
          fontStyle: 'bold',
          // whole-word default; setCellLabel's two-pass fitter owns the real wrap mode per size.
          wordWrap: { width: w - 8, useAdvancedWrap: false },
        })
        .setOrigin(0.5)
        .setStroke('#020617', 2)
        // Stroke + shadow so country names read on ANY tile art in motion.
        .setShadow(0, 1, '#0a0b12', 3, false, true);
      // status glyph (✓/✗ for eaten/scanned/safe-pass) — corner or center, hidden until needed.
      const glyph = this.add
        .text(w / 2 - 3, -h / 2 + 2, '', {
          fontFamily: FONT.family,
          fontSize: `${Math.max(11, Math.min(18, Math.floor(w * 0.34)))}px`,
          color: hex(PALETTE.text),
          fontStyle: 'bold',
        })
        .setOrigin(1, 0)
        .setVisible(false);
      const stateFrame = this.add.graphics().setVisible(false);
      const labelPlate = this.add.graphics().setVisible(false);
      // Centred padlock icon (Graphics) for the armed / spent-decoy "locked" tile state. Drawn once,
      // hidden until the cell arms; the amber lock + amber ring + dark lockFill read as "do not enter".
      const lockIcon = this.add.graphics().setVisible(false);
      // LIAR TELL: a thin magenta-ish glitch band that shimmers across the tile + a ⚠ warning glyph
      // in the corner. Built once, hidden until the cell's snapshot reports `lying === true`. Kept at
      // low alpha so the (still-readable) label sits clearly above it.
      const glitchOverlay = this.add
        .rectangle(0, 0, w, h, PALETTE.ai, 0.0)
        .setVisible(false);
      const lieGlyph = this.add
        .text(-w / 2 + 3, -h / 2 + 2, '⚠', {
          fontFamily: FONT.family,
          fontSize: `${Math.max(13, Math.min(22, Math.floor(w * 0.38)))}px`,
          color: hex(PALETTE.gold),
          fontStyle: 'bold',
        })
        .setOrigin(0, 0)
        .setStroke('#020617', 3)
        .setShadow(0, 1, '#020617', 3, false, true)
        .setVisible(false);
      // A static "gel/satin" material lifts the flat tile into a raised, eatable OBJECT: a graded top
      // sheen (a few stacked highlight bands fading down), a slightly stronger bottom inner shadow, and
      // a 1px rounded inner rim. It sits above the bg/tint and below the label, so depth reads as a
      // tappable physical tile without ever darkening the text band (the rim/shadow hug the edges; the
      // centre — where the label lives — stays clean). Drawn once per cell (static, not per-frame).
      const bevel = this.add.graphics();
      this.paintTileGel(bevel, w, h);
      // IDLE LIFE (motion only): a faint specular band hugging the upper third of the tile. ONE update
      // handler (built after this loop) breathes its alpha with a per-cell phase offset so the grid
      // shimmers like a slow diagonal wave between moves. Additive + very low alpha, parked over the
      // gel sheen and BELOW the label, so it reads as ambient light on the slab and never touches the
      // answer-text contrast. Absent entirely under reduced-motion (the static gel carries the depth).
      let idleSheen: Phaser.GameObjects.Rectangle | undefined;
      if (motionAllowed()) {
        // Sheen tinted a step toward the board theme accent so the idle wave also carries the
        // per-rung mood (still additive + low alpha — label contrast holds ≥4.5:1, see decisions).
        idleSheen = this.add
          .rectangle(0, -h / 2 + Math.max(3, h * 0.16), w - 6, Math.max(4, h * 0.3), mixColor(0xffffff, theme.accent, 0.3), 0)
          .setBlendMode(Phaser.BlendModes.ADD);
        // diagonal wave: phase rises with (col+row) so the shimmer sweeps across the board, not in sync.
        const col = cell.id % this.cols;
        const row = Math.floor(cell.id / this.cols);
        this.idleSheens.push({ sheen: idleSheen, phase: (col + row) * 0.55 });
      }
      // tileImg first (behind), then bg tint, bevel, overlays, then text on top — order preserves legibility.
      const children: Phaser.GameObjects.GameObject[] = [];
      if (tileImg) children.push(tileImg);
      // glitchOverlay sits ABOVE the bg tint but BELOW the label so text stays readable; lieGlyph on top.
      children.push(bg, bevel);
      if (idleSheen) children.push(idleSheen);
      children.push(glitchOverlay, stateFrame, labelPlate, label, glyph, lockIcon, lieGlyph);
      container.add(children);
      const view: CellView = {
        container,
        bg,
        tileImg,
        label,
        stateFrame,
        labelPlate,
        glyph,
        lockIcon,
        glitchOverlay,
        lieGlyph,
        idleSheen,
        w,
        h,
        mode: 'neutral',
        scanned: false,
        fittedText: '',
        lying: false,
        lastDisplayText: cell.kind === 'wall' ? '' : cell.displayText,
      };
      this.cellViews[cell.id] = view;
      // Initial shrink-to-fit so the first frame / create()-time screenshot is already legible.
      if (cell.kind !== 'wall') {
        this.setCellLabel(view, cell.displayText);
      } else {
        // BLOCKED tiles are navigation blockers, not unknown answers. The old dot-only socket read as an
        // unlabeled placeholder in still review, so walls now carry a no-entry mark plus a compact label.
        bg.setFillStyle(PALETTE.wall, 1);
        bg.setStrokeStyle(1.5, PALETTE.tileBorder, 0.9);
        const blocked = this.add.graphics();
        paintBlockedSocketMark(blocked, 0, 0, w, h, this.themeCellStroke);
        const blockedText = makeBlockedSocketText(this, 0, h * 0.28, w);
        container.add([blocked, blockedText]);
      }
    }

    // Empty-slot VOID tiles (S4 STAGE): a 6×6 board packs 35 items into 36 cells, so one grid position
    // has no cell in snap.cells. Left bare it reads as a MISSING tile (a hole punched in the frame).
    // Paint an intentional recessed SOCKET there instead — a darker inset with the same theme-cell rim as
    // the live tiles plus a faint grid-dust dot cluster (echoing the backdrop motes) — so the void reads
    // as a designed empty slot that belongs to the tile kit, never a bug. Covers ANY count of empty
    // positions; walls are real cells (already in cellViews) so they're skipped.
    const slotCount = this.rows * this.cols;
    for (let id = 0; id < slotCount; id++) {
      if (this.cellViews[id]) continue;
      const { x, y } = this.cellCenter(id);
      const w = this.cellW - 4;
      const h = this.cellH - 4;
      const socket = this.add.graphics().setDepth(2.85);
      paintBlockedSocketMark(socket, x, y, w, h, this.themeCellStroke);
      makeBlockedSocketText(this, x, y + h * 0.28, w).setDepth(2.9);
    }

    this.installIdleBoardLife(boardCx, boardCy, panelW, panelH, boardH);
  }



  /**
   * IDLE BOARD LIFE (renderer-only, motion-gated). Between moves the board is otherwise static; this
   * gives it a slow, low-amplitude breath so it reads alive without ever competing with the labels:
   *   1) a per-cell specular SHEEN whose alpha is breathed by ONE scene-UPDATE handler with a per-cell
   *      phase offset, so the highlight shimmers diagonally across the grid like a slow wave (no
   *      per-frame allocation — we only mutate already-built rects' alpha, never create objects),
   *   2) a single faint diagonal SPECULAR BAND that drifts across the whole slab on a slow infinite
   *      tween (one tween-driven object, recycled — no per-frame work), reading as light passing over.
   * Both are skipped entirely under reduced-motion (no objects, no handler) so the static gel tiles
   * carry the premium read with zero movement — a reduced-motion player gets a clean, legible board.
   */
export function installIdleBoardLife(this: GameScene,
    boardCx: number,
    boardCy: number,
    panelW: number,
    panelH: number,
    boardH: number,
  ): void {
    if (!motionAllowed()) return;

    // 1) per-cell sheen breath — a single cheap UPDATE handler over the already-built sheen rects.
    if (this.idleSheens.length > 0) {
      const onSheen = (_t: number, dt: number) => {
        this.idleSheenElapsed += dt;
        const s = this.idleSheenElapsed / 1000;
        for (const { sheen, phase } of this.idleSheens) {
          if (!sheen.active) continue;
          // slow 0.55 Hz-ish breath, peak ~0.09 alpha — raised from 0.06 so the idle wave is
          // actually perceptible at phone size / 540p capture (reviewers read the old board as
          // static), still ambient enough to never compete with the answer labels.
          const a = 0.025 + 0.065 * (0.5 + 0.5 * Math.sin(s * 1.1 - phase));
          sheen.setAlpha(a);
        }
      };
      this.events.on(Phaser.Scenes.Events.UPDATE, onSheen, this);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
        this.events.off(Phaser.Scenes.Events.UPDATE, onSheen, this),
      );
    }

    // 2) slow drifting diagonal specular band over the whole slab — one tween-driven object, looped.
    // Tinted toward the board theme accent (per-rung mood) and a notch brighter than the old pure
    // white 0.05 so the pass reads at phone size; still under the cells, never over labels.
    const left = boardCx - panelW / 2;
    const right = boardCx + panelW / 2;
    const bandW = Math.max(26, panelW * 0.2);
    const bandPeakA = 0.07; // additive band peaks mid-slab; fades to 0 at BOTH travel extremes (#4 fix, below)
    const bandTravel = right - left + bandW * 2; // full sweep span: x runs (left-bandW) → (right+bandW)
    const band = this.add
      .rectangle(left - bandW, boardCy, bandW, boardH * 1.1, mixColor(0xffffff, this.boardTheme().accent, 0.5), 0)
      .setDepth(2.6) // above the board-surface ambient (2.5), below the cells (3) — never over labels.
      .setAngle(14)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: band,
      x: right + bandW,
      duration: 5200,
      ease: 'Sine.easeInOut',
      repeat: -1,
      repeatDelay: 3400, // long dark gap between passes so it's a rare glint, not a strobe.
      // #4 desktop right-gutter light-shaft: this band PARKS at x=right+bandW through the 3400ms repeatDelay,
      // which on desktop sits in the right gutter (mobile has none) — a static alpha painted a permanent
      // diagonal streak. Alpha-envelope the sweep (0 → peak → 0 across the travel) so the band is INVISIBLE
      // at both extremes; the gutter park now shows nothing. Sweep/ease/duration/repeat are unchanged.
      onUpdate: () =>
        band.setAlpha(bandPeakA * Math.sin(Math.PI * Phaser.Math.Clamp((band.x - (left - bandW)) / bandTravel, 0, 1))),
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (band.active) band.destroy();
    });
  }



  /**
   * Paint the per-cell "gel/satin" material into a (static) Graphics: a soft raised read that makes an
   * un-eaten tile look tappable/eatable instead of a flat outlined button. Three cheap, edge-hugging
   * layers, NONE of which intrude on the centred label band:
   *   1) a graded TOP sheen — a few stacked white bands fading downward over the upper ~third,
   *   2) a slightly stronger BOTTOM inner shadow — a couple of dark bands hugging the lower edge,
   *   3) a 1px rounded inner RIM — bright on top/left, dark on bottom/right — so the tile has an edge.
   * All low-alpha and confined to the rim/top/bottom; the middle (where text sits) is untouched so
   * label contrast is preserved. Static — drawn once in buildGrid, never per-frame.
   */
export function paintTileGel(this: GameScene, g: Phaser.GameObjects.Graphics, w: number, h: number): void {
    const inset = 2;
    const x0 = -w / 2 + inset;
    const y0 = -h / 2 + inset;
    const iw = w - inset * 2;
    const ih = h - inset * 2;
    const radius = Math.min(8, iw * 0.18);

    // 1) Graded top sheen: 4 thin bands from the top edge, each fainter + shorter, for a rounder gel
    // crown (M3: was 3 bands @0.07 — one more step + stronger so the tile reads as a raised, glossy
    // OBJECT, not a flat outlined button). Confined to the upper ~third; the centred label is untouched.
    const sheenH = Math.max(3, ih * 0.34);
    const bands = 4;
    for (let i = 0; i < bands; i++) {
      const a = 0.088 * (1 - i / bands); // 0.088 -> ~0.022, fading down
      const bandY = y0 + (sheenH * i) / bands;
      const bandH = sheenH / bands + 0.5;
      g.fillStyle(0xffffff, a);
      g.fillRect(x0, bandY, iw, bandH);
    }

    // 1b) INNER TOP HIGHLIGHT (M3): a crisp specular lip just inside the top edge — the glossy "wet"
    // crown the mock tiles carry. One bright hairline over the sheen, still well above the label band.
    g.fillStyle(0xffffff, 0.22);
    g.fillRect(x0 + radius * 0.6, y0 + 1.5, iw - radius * 1.2, Math.max(1, ih * 0.028));

    // 1c) TOP-SLOT DETAIL (M3, mock-2): a short recessed "cartridge slot" centred in the top ~10% — a
    // dark inset bar with a thin highlight beneath, reading as a machined tile. Well clear of the centred
    // label; decoration only, static.
    const slotW = iw * 0.3;
    const slotH = Math.max(2, ih * 0.035);
    const slotY = y0 + Math.max(3, ih * 0.1);
    g.fillStyle(0x000000, 0.22);
    g.fillRoundedRect(-slotW / 2, slotY, slotW, slotH, slotH / 2);
    g.fillStyle(0xffffff, 0.1);
    g.fillRoundedRect(-slotW / 2, slotY + slotH, slotW, 1, 0.5);

    // 2) Bottom inner shadow: 2 bands hugging the lower edge, the lowest darkest, for grounded weight.
    // M3: deepened (0.16/0.24 -> 0.2/0.32) so the tile sits with more physical weight on the slab.
    const shadeH = Math.max(3, ih * 0.2);
    g.fillStyle(0x000000, 0.2);
    g.fillRect(x0, y0 + ih - shadeH, iw, shadeH * 0.6);
    g.fillStyle(0x000000, 0.32);
    g.fillRect(x0, y0 + ih - shadeH * 0.45, iw, shadeH * 0.45);

    // 3) rounded rim — light top/left highlight, dark bottom/right shadow. M3: thickened to 1.5px and
    // pushed in contrast (0.18/0.28 -> 0.26/0.4) so the raised bevel reads clearly at phone size.
    g.lineStyle(1.5, 0xffffff, 0.26);
    g.beginPath();
    g.moveTo(x0 + radius, y0);
    g.lineTo(x0 + iw - radius, y0); // top edge
    g.moveTo(x0, y0 + radius);
    g.lineTo(x0, y0 + ih - radius); // left edge
    g.strokePath();
    g.lineStyle(1.5, 0x000000, 0.4);
    g.beginPath();
    g.moveTo(x0 + radius, y0 + ih);
    g.lineTo(x0 + iw - radius, y0 + ih); // bottom edge
    g.moveTo(x0 + iw, y0 + radius);
    g.lineTo(x0 + iw, y0 + ih - radius); // right edge
    g.strokePath();
  }



  /**
   * TEXT FIT (M2 step 2b + whole-word pass): shrink-to-fit + wrap-to-2-lines (3 lines allowed only
   * in the sub-10px relaxed stage) so ANY label up to MAX_CELL_CHARS=22 stays FULLY legible inside
   * its tile at 360px-wide / 5 cols, with NO truncation/ellipsis. We measure the actually-rendered
   * text (Phaser GetTextSize via the live Text object) and step the font size down until both the
   * wrapped width AND height fit the cell box. The whole answer is the thing being judged, so it
   * must always be readable — we never cut it, we only scale it; the floor is 9px (phone floor).
   *
   * WHOLE-WORD-FIRST (store-capture fix): the old fitter used ADVANCED word wrap at EVERY size, so
   * a label like "Washington DC" could render as "Washingt/on DC" the moment the advanced wrapper
   * found a mid-word break at a bigger font. Now PASS 1 tries every size with whole-word wrapping
   * only (advanced=false — an over-long word simply fails the width check and we shrink further);
   * PASS 2 re-allows mid-word breaking only at small sizes (<= WORD_BREAK_MAX_PX) as the last
   * resort before the pinned floor. Evidence (outputs/tmp/label-fit-histogram.mjs fit-replay over
   * all 3467 seed-pack labels at the 720x1280 design space): with whole words the worst REAL label
   * still fits at 15px on 5x6 phone / 12px on 6x6 tablet, and ZERO real labels ever reach the
   * mid-word pass on either grid — the break path survives purely as a pathological-input safety
   * net (a 22-char single word pins at 11px/9px there).
   *
   * Idempotent + cached on the view (skips re-fitting when the text is unchanged) so the per-frame
   * render loop stays cheap.
   */
export function setCellLabel(this: GameScene, v: CellView, text: string): void {
    if (v.fittedText === text) {
      if (v.label.text !== text) v.label.setText(text);
      return;
    }
    v.fittedText = text;
    if (text === '') {
      v.label.setText('');
      return;
    }

    // Cell box available to text (leave a small inner margin so glyphs never kiss the border).
    const boxW = v.w - 4;
    // Allow text to use most of the tile height; cap the wrapped block at 2 lines.
    const boxH = v.h - 6;

    // Start big (relative to the tile) and shrink. Floor is 9px — the phone legibility floor
    // (Gemini reviews flag anything smaller as unreadable). See the whole-word evidence note above:
    // the floor is a pure safety net, never a clamp that clips real content.
    const startSize = Math.max(12, Math.min(22, Math.floor(v.w * 0.48)));
    const minSize = 9;
    const singleLongWord = !/\s/.test(text.trim()) && text.trim().length >= 10;

    const label = v.label;
    label.setCrop(); // labels are pooled across boards — clear any prior worst-case crop
    label.setText(text);
    label.setLineSpacing(-1);
    // One candidate-size attempt. Below 10px the fitter relaxes BEFORE giving up: the wrap width
    // widens to w-4 (still inside the tile bg) and a 3rd wrapped line is allowed. The height check
    // keeps the block inside boxH; the un-fitted fallback below adds a hard crop for the
    // unreachable worst case.
    const tryFit = (size: number, allowWordBreak: boolean): boolean => {
      const relaxed = size < 10;
      const wrapW = relaxed ? v.w - 4 : boxW;
      const maxLines = relaxed ? 3 : 2;
      label.setFontSize(size);
      label.setWordWrapWidth(wrapW, allowWordBreak);
      const lines = label.getWrappedText(text);
      // Require the line cap AND both dimensions inside the cell box. With whole-word wrapping an
      // over-long single word stays on one line and simply fails the width check here.
      return lines.length <= maxLines && label.height <= boxH + 1 && label.width <= wrapW + 1;
    };
    // PASS 1 — whole words only, at every size: a smaller font with intact words ALWAYS beats a
    // bigger font with a mid-word break ("Washingt/on DC" is a store-visible defect).
    let fitted = false;
    for (let size = startSize; size >= minSize; size--) {
      if (tryFit(size, false)) {
        fitted = true;
        break;
      }
    }
    // PASS 1b — one long token: allow a clean two-line break before shrinking to tiny text. Multi-word
    // labels still prefer whole-word wrapping so "Washington DC" never becomes "Washingt/on DC".
    if (!fitted && singleLongWord) {
      for (let size = startSize; size >= minSize; size--) {
        if (tryFit(size, true)) {
          fitted = true;
          break;
        }
      }
    }
    // PASS 2 — mid-word breaking, small sizes only: the last resort before the floor, for a single
    // word too long to fit whole at ANY legible size (unreached by real seed labels — see above).
    if (!fitted) {
      for (let size = Math.min(startSize, WORD_BREAK_MAX_PX); size >= minSize; size--) {
        if (tryFit(size, true)) {
          fitted = true;
          break;
        }
      }
    }
    if (!fitted) {
      // Extreme worst case (not reachable within MAX_CELL_CHARS=22 — see the evidence note above):
      // pin to the 9px floor at the widest wrap, and hard-crop any residual bottom overflow so even
      // pathological text can never escape the tile box.
      label.setFontSize(minSize);
      label.setWordWrapWidth(v.w - 4, true);
      if (label.height > boxH) label.setCrop(0, 0, label.width, boxH);
    }
  }



export function cellCenter(this: GameScene, id: number): { x: number; y: number } {
    const col = id % this.cols;
    const row = Math.floor(id / this.cols);
    return {
      x: this.gridX + col * this.cellW + this.cellW / 2,
      y: this.gridY + row * this.cellH + this.cellH / 2,
    };
  }
